import { JobLocationContext, JobTravelExpectations } from '@factoryfixinc/ats-interfaces';

import { isFieldContext } from './location-context.utils';

// Job boards such as Indeed read the work arrangement from the description itself, so a field
// job gets one plain paragraph the customer can edit. Written once, then only replaced while it
// still reads exactly as we wrote it (ONE-7106, David's review).
const WHERE_SENTENCE: Partial<Record<JobLocationContext, string>> = {
  [JobLocationContext.LIVES_NEAR]:
    'This is a field role: the work happens at customer and job sites rather than at a single ' +
    'worksite, and you will be based within driving distance of the job location.',
  [JobLocationContext.NATIONWIDE]:
    'This is a field role: the work happens at customer and job sites across the U.S., ' +
    'and you can live anywhere in the country.',
};

const TRAVEL_SENTENCE: Record<JobTravelExpectations, string> = {
  [JobTravelExpectations.DAY_TRIPS_HOME_MOST_NIGHTS]:
    'Expect day trips to job sites, with most nights at home.',
  [JobTravelExpectations.OVERNIGHT_HOME_MOST_WEEKENDS]:
    'Expect overnight travel during the week, with most weekends at home.',
  [JobTravelExpectations.ASSIGNMENTS_1_3_MONTHS]:
    'Expect extended assignments of one to three months at a time.',
  [JobTravelExpectations.ASSIGNMENTS_3_PLUS_MONTHS]:
    'Expect extended assignments of three months or more at a time.',
  [JobTravelExpectations.VARIES]:
    'Travel varies by assignment; the details are shared for each project.',
};

const TRAVEL_TERMS = new RegExp(
  [
    'travels?',
    'travell?ing',
    'overnights?',
    'on the road',
    '(job|customer|work|client) sites?',
    'remote',
    'nationwide',
    'per diem',
    'assignments?',
    'home most',
  ]
    .map((term) => `\\b${term}\\b`)
    .join('|'),
  'i',
);

/** The sentence pair for a field job, or null when the job is a worksite or has no answer yet. */
export function travelDisclosureText(
  context: JobLocationContext | null | undefined,
  travelExpectations: JobTravelExpectations | null | undefined,
): string | null {
  if (!isFieldContext(context) || !travelExpectations) return null;
  const where = WHERE_SENTENCE[context as JobLocationContext];
  return where ? `${where} ${TRAVEL_SENTENCE[travelExpectations]}` : null;
}

export function mentionsTravel(descriptionHtml: string | null | undefined): boolean {
  const text = (descriptionHtml ?? '').replace(/<[^>]*>/g, ' ');
  return TRAVEL_TERMS.test(text);
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// The paragraph as we wrote it, tolerant of the attributes and whitespace an editor adds.
const paragraphPattern = (text: string): RegExp =>
  new RegExp(`\\s*<p[^>]*>\\s*${escapeRegExp(text)}\\s*</p>\\s*`, 'i');

export type TravelDisclosureSync = {
  description: string;
  /** The text of the paragraph currently in the description that we wrote, if any. */
  inserted: string | null;
};

/**
 * Bring the description in line with the current answers without touching the customer's words.
 * Our own paragraph, still unedited, is replaced (or removed when the job is no longer a field
 * job). Anything the customer changed stays; a description that already talks about travel is
 * left alone so it is never duplicated or contradicted.
 */
export function syncTravelDisclosure(
  current: TravelDisclosureSync,
  next: string | null,
): TravelDisclosureSync {
  const { description, inserted } = current;

  if (inserted) {
    const pattern = paragraphPattern(inserted);
    if (pattern.test(description)) {
      if (next === inserted) return current;
      const replaced = description.replace(pattern, next ? `\n<p>${next}</p>\n` : '\n').trim();
      return { description: replaced, inserted: next };
    }
  }

  if (!next || mentionsTravel(description)) return { description, inserted: null };

  const body = description.trim();
  return { description: body ? `${body}\n<p>${next}</p>` : `<p>${next}</p>`, inserted: next };
}
